/**
 * Blog post: ÖSD B1 exam guide.
 * Describes the ZDÖ B1 (Zertifikat Deutsch Österreich), the format our ÖSD B1 mock
 * exams follow: one written part worth 225 points plus a separate oral exam.
 * Content: which ÖSD B1, structure and points, pass mark, preparation, Redemittel link, tips.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

const PARTS = [
  { name: 'Leseverstehen', en: 'Reading', points: 75 },
  { name: 'Sprachbausteine', en: 'Language elements', points: 30 },
  { name: 'Hörverstehen', en: 'Listening', points: 75 },
  { name: 'Schreiben', en: 'Writing: one email', points: 45 },
  { name: 'Sprechen', en: 'Speaking, with a partner', points: 75 },
];

export default function OsdB1ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        This guide covers the <strong>ÖSD Zertifikat Deutsch Österreich B1 (ZDÖ B1)</strong>: how the exam is built,
        how many points each part is worth, what you need to pass, and how to prepare. It is the format our ÖSD B1
        mock exams follow.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Which ÖSD B1 Are You Taking?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        ÖSD runs two B1 exams, and they are built differently. The <strong>ZDÖ B1</strong> is ÖSD&apos;s own exam,
        with one written part and a separate oral exam. ÖSD also offers the <strong>Zertifikat B1</strong> together
        with the Goethe-Institut, which uses the Goethe B1 format of four separate modules. Check which one your
        registration names before you plan your preparation. Everything below is about the ZDÖ B1.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Structure and Points: 225 Written, 75 Oral
      </h2>
      <div className="my-6 p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
          The <strong>ÖSD B1</strong> written part has four sections worth <strong>225 points</strong> together. The
          oral exam is worth another 75, for 300 in total.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mb-6">
          {PARTS.map((part) => (
            <div key={part.name} className="p-4 rounded-2xl border-2 border-black/10 bg-white text-center">
              <p className="font-space font-bold text-black mb-1">{part.name}</p>
              <p className="text-sm text-black/70">{part.en}</p>
              <p className="font-space font-bold text-black mt-2">{part.points} points</p>
            </div>
          ))}
        </div>
        <p className="text-base md:text-lg text-black/70 leading-relaxed">
          The written part and the oral exam are graded separately, and you have to pass both.
        </p>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How Many Points Do You Need to Pass?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        You pass the written part with at least <strong>135 of 225 points</strong> (60%). There is no separate
        minimum for each section, so points you gain in Leseverstehen can make up for a weaker Hörverstehen. The oral
        exam needs at least 45 of its 75 points.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Preparing with Mock Exams and Redemittel
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The most reliable preparation is a complete <strong>ÖSD B1 Modelltest</strong> under real timing, followed by
        an honest look at where you lost points. Schreiben is worth 45 points for a single email, so the phrases for
        formal and informal emails are worth learning by heart.
      </p>
      <div className="my-10 p-6 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <h3 className="font-space text-lg font-bold text-black mb-3">Free ÖSD B1 Redemittel</h3>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          The phrases for the email and the oral exam, sorted by task, with English meanings, example sentences and
          the complete list as a free PDF.
        </p>
        <Link
          to={`${blogBase}/osd-b1-redemittel-schreiben`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-black hover:bg-black/90 text-white font-space font-bold rounded-xl border-2 border-black shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Get the ÖSD B1 Redemittel (free PDF)
        </Link>
      </div>
      <SprechenSamplesCallout provider="osd" level="b1" />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Tips for the ÖSD B1
      </h2>
      <ul className="list-disc list-inside space-y-3 text-base md:text-lg text-black/70 leading-relaxed mb-10 ml-2">
        <li><strong>Use the shared total:</strong> since the written sections share one pass mark, make sure your strongest sections are as strong as they can be, rather than spreading your time evenly.</li>
        <li><strong>Get the register right:</strong> read the email task carefully and decide straight away whether it is formal or informal. The opening and closing set the tone for the whole text.</li>
        <li><strong>Learn the topics:</strong> work, health, housing, leisure and the media come up again and again at B1.</li>
        <li><strong>Practise against the clock:</strong> sit at least one full mock exam in one go, so the timing on exam day does not surprise you.</li>
      </ul>

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-[#1e293b] text-white">
        <h2 className="font-space text-xl font-bold text-white mb-3">
          Ready to Try a Full ÖSD B1 Mock Exam?
        </h2>
        <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
          Logami is built by German teachers and has 4 complete ÖSD B1 mock exams in the ZDÖ B1 format, with AI
          feedback on your email. Your first mock exam is free.
        </p>
        <Link
          to={`/${lang}/mock-exams/osd-b1`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-space font-bold rounded-xl border-2 border-emerald-500 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          See the ÖSD B1 mock exams
        </Link>
      </div>

      <RelatedResources provider="osd" level="b1" variant="exam-guide" />
    </article>
  );
}
